/**
 * The injected readonly `types.ts` module for rule workspaces: the
 * rule-visible contract as plain TypeScript types (a locked editor tab, like
 * CSP's). Mirrors the zod contract in ig-types
 * functions/agentic-logic/{types,runtime}.ts — the zod schemas stay the source
 * of truth; this is their editor-facing projection. A colocated spec
 * transpiles this source so a syntax slip can never reach a workspace.
 *
 * This is also where the AMBIENT SDK's own types live (the "the ambient SDK"
 * section): `AgenticLogicSdk` and the three chain-combinator signatures. The
 * injected `sdk.d.ts` is only four `declare const` lines pointing back here —
 * see {@link AGENTIC_LOGIC_SDK_AMBIENT_DTS} for why the split runs that way.
 *
 * The org-data surface — `sdk.getDataset` with its `AgenticLogicDatasetKey`
 * union, and `sdk.getSource` — is emitted only for an author who may use it
 * (see {@link AgenticLogicDataSurface}). The key union is the org's authored
 * dataset keys at generation time, so a typo'd sdk.getDataset(key) fails at
 * the type gate instead of at runtime. Payload types stay the caller-supplied
 * generic — datasets declare their shapes as stored JSON Schema, deliberately
 * NOT re-projected into TypeScript.
 *
 * Conventions restated for rule authors: ids are Shopify GIDs; ALL money is
 * integer minor units with an ISO-4217 currency alongside; timestamps are
 * ISO-8601 UTC strings.
 */
import type { AgenticLogicSdkNotedField } from "@intelligems/ig-types";

/**
 * Whether the author of a rule may use the org-data surface — datasets
 * (`sdk.getDataset`) and Sources (`sdk.getSource`) — and, when they may, the
 * org's authored dataset keys.
 *
 * The surface is for Intelligems admins only. Every generation-time caller
 * decides this from the ACTING user's admin status and passes it explicitly:
 * a merchant's projection carries neither accessor, so neither the Monaco
 * editor nor the authoring model can learn the store exists. The node-backend
 * routes and the web/ai tools enforce the same rule on their own side; this is
 * the author-facing projection of it, not the enforcement.
 *
 * The runtime SDK is not gated: a published rule that reads a dataset or a
 * Source keeps evaluating, whoever the merchant is.
 */
export type AgenticLogicDataSurface =
  | { enabled: false }
  | { enabled: true; datasetKeys: readonly string[] };

/** The projection for an author who may not use the org-data surface. */
export const AGENTIC_LOGIC_DATA_SURFACE_OFF: AgenticLogicDataSurface = {
  enabled: false,
};

export interface AgenticLogicTypesModuleSourceOptions {
  /**
   * Whether the author gets `sdk.getDataset` and `sdk.getSource`, and the
   * org's dataset keys when they do. Required: a new call site must decide it
   * from the acting user, never inherit a default.
   */
  dataSurface: AgenticLogicDataSurface;
  /**
   * Per-field notes on where each surface-dependent request block can actually
   * be read, keyed by field name, so a hover over `request.order` in the editor
   * says which surfaces send it.
   *
   * PASSED IN rather than computed here: this package may import only types
   * from `@intelligems/ig-types`, and the availability table and the
   * descriptions it renders from are runtime values. The caller renders them
   * with `renderAgenticLogicRequestFieldNotes` and hands the result over, which
   * keeps the one source of truth in ig-types and keeps this module a pure
   * string builder.
   *
   * Optional, and a missing note emits no comment rather than an empty one.
   * These are documentation: a caller that has nothing to say about a field
   * must still get valid TypeScript out, so this can never be load-bearing.
   */
  requestFieldNotes?: Readonly<Record<string, string>>;
  /**
   * Notes for the SDK fields whose meaning lives in the zod `.describe()` this
   * projection strips — the scan and limit bounds, the price comparators, the
   * page's scan telemetry, and the zero-slot context.
   *
   * Same seam and same reason as {@link requestFieldNotes}: the ceilings are
   * runtime constants in `@intelligems/ig-types`, which this package may not
   * import as VALUES, so the caller renders them with
   * `renderAgenticLogicSdkFieldNotes` and hands the result over. Optional and
   * never load-bearing for the same reason: the data-surface-off bundler constant
   * below emits valid TypeScript with no notes at all.
   *
   * The KEY set is typed against ig-types' coverage table, so a note site here
   * that names a field the table does not know about is a build error — the
   * projection and the completeness invariant cannot drift apart.
   */
  sdkFieldNotes?: Readonly<Partial<Record<AgenticLogicSdkNotedField, string>>>;
}

/**
 * The minimal raw Storefront operation `assertContextBoundRawQuery` accepts.
 * It is pasted verbatim into the model-facing projection as the example, and
 * exported so the runtime spec can prove the DOCUMENTED example still passes
 * the validator — the doc and the validator cannot drift apart silently.
 */
export const AGENTIC_LOGIC_RAW_QUERY_EXAMPLE_OPERATION =
  "query RawProducts($country: CountryCode!) @inContext(country: $country) { products(first: 10) { nodes { id title } } }";

/**
 * What {@link AGENTIC_LOGIC_RAW_QUERY_EXAMPLE_OPERATION} actually RESOLVES to:
 * the operation's own root fields, one level, with NO `data` key — the
 * `{data, errors}` envelope is stripped by the transport
 * (`ShopifyStorefrontClient.executeQuerySource` returns `result.data`) long
 * before the SDK sees it.
 *
 * Like its operation pair it is pasted verbatim into the model-facing
 * projection AND asserted by the runtime spec, so the documented shape and the
 * shape `getProductsRaw` resolves cannot drift apart silently.
 */
export const AGENTIC_LOGIC_RAW_QUERY_EXAMPLE_RESULT =
  '{ "products": { "nodes": [{ "id": "gid://shopify/Product/1", "title": "…" }] } }';

/**
 * The canonical INSTANCED-ATTEMPT idiom: one static base, one candidate per
 * element of a list, the element carried as `{ instance }`.
 *
 * Pasted verbatim into the model-facing projection AND into both authoring
 * prompts, and exported for the same reason the raw-query example is: specs
 * prove the DOCUMENTED example is the shape the runtime records, the graph
 * check accepts, and the ladder pricer can prove bounded — so the doc and the
 * three implementations cannot drift apart silently.
 *
 * `themes` is a `const themes = ["…", "…", "…"]` array literal in the rule
 * source. That matters to the price, not just to the reader — see the
 * `AgenticLogicAttemptFn` contract rendered below.
 */
export const AGENTIC_LOGIC_INSTANCED_ATTEMPT_EXAMPLE =
  'firstNonEmpty(themes.map((theme) => attempt("theme", () => productsForTheme(theme), { instance: theme })))';

export function buildAgenticLogicTypesModuleSource({
  dataSurface,
  requestFieldNotes = {},
  sdkFieldNotes = {},
}: AgenticLogicTypesModuleSourceOptions): string {
  /** A doc comment for a field, or nothing when the caller supplied no note. */
  const note = (field: string): string => {
    const text = requestFieldNotes[field];
    return text ? `/** ${text} */\n  ` : "";
  };
  /** The same, for the SDK-side notes. */
  const sdkNote = (field: AgenticLogicSdkNotedField): string => {
    const text = sdkFieldNotes[field];
    return text ? `/** ${text} */\n  ` : "";
  };
  const datasetKeyType = dataSurface.enabled
    ? `/**
 * The org's authored dataset keys at generation time. sdk.getDataset(key)
 * only accepts these, so a typo'd key fails the type gate instead of
 * failing at runtime. Payload shapes are declared by each dataset's stored
 * JSON Schema — supply the expected type via the getDataset generic.
 */
export type AgenticLogicDatasetKey = ${
        dataSurface.datasetKeys.length > 0
          ? [...dataSurface.datasetKeys]
              .sort()
              .map((key) => JSON.stringify(key))
              .join(" | ")
          : "never"
      };

`
    : "";
  const getDatasetAccessor = dataSurface.enabled
    ? `getDataset<T = unknown>(key: AgenticLogicDatasetKey): Promise<T>;
  `
    : "";
  const getSourceAccessor = dataSurface.enabled
    ? `/**
   * A named Source: an org-defined, input-independent catalog read (best
   * sellers, new arrivals, in stock, or one collection page) whose answer the
   * platform keeps cached and refreshed on a schedule. Resolves the same
   * products the equivalent direct read would, usually without a Shopify
   * call. An unknown name REJECTS, and so does a GraphQL Source.
   */
  getSource(name: string): Promise<AgenticLogicProduct[]>;
  /**
   * A named GraphQL Source (storefrontGraphql / adminGraphql): an org-defined
   * stored query whose answer the platform keeps cached and refreshed. Resolves
   * the query's \`data\` OBJECT verbatim and UNVALIDATED, exactly like
   * getProductsRaw — narrow it yourself. A Storefront one is read in the
   * shopper's market. An unknown name, or a typed Source, REJECTS.
   */
  getSourceRaw(name: string): Promise<unknown>;
  `
    : "";
  return `// Injected by the Intelligems build — read-only contract types.

// ----- shared -----
export type AgenticLogicSelectedOption = { name: string; value: string };

// ----- request side -----
export type AgenticLogicCartLineFlags = {
  giftCard: boolean | null;
  syntheticGift: boolean | null;
  bundleChild: boolean | null;
  igUpsell: boolean | null;
};

export type AgenticLogicCartLine = {
  productId: string;
  variantId: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  selectedOptions: AgenticLogicSelectedOption[];
  isSubscriptionLine: boolean;
  flags: AgenticLogicCartLineFlags;
};

export type AgenticLogicCartData = {
  currency: string;
  subtotal: number;
  itemCount: number;
  lines: AgenticLogicCartLine[];
};

export type AgenticLogicOrderLineDiscountAllocation = { amount: number; title?: string };

export type AgenticLogicOrderLine = AgenticLogicCartLine & {
  title?: string;
  vendor?: string;
  productType?: string;
  sellingPlanId?: string;
  discountAllocations?: AgenticLogicOrderLineDiscountAllocation[];
};

export type AgenticLogicOrderData = {
  orderId: string;
  checkoutToken?: string;
  createdAt: string;
  currencyCode: string;
  presentmentCurrency: string;
  itemsSubtotal: number;
  discountTotal?: number;
  shippingTotal?: number;
  taxTotal?: number;
  total: number;
  lines: AgenticLogicOrderLine[];
};

export type AgenticLogicPageProduct = { productId: string; handle: string };

export type AgenticLogicCustomerAmountSpent = { amount: number; currency: string };

export type AgenticLogicCustomerData = {
  id: string;
  ordersCount: number | null;
  amountSpent?: AgenticLogicCustomerAmountSpent;
};



export type AgenticLogicShippingTier = { threshold: number; label: string };

export type AgenticLogicShippingDeliveryOption = {
  title?: string;
  isPickup: boolean;
  selected: boolean;
  cost?: number;
  currency?: string;
  carrierName?: string;
  transitDays?: { min?: number; max?: number };
};

export type AgenticLogicShippingDelivery = {
  isLocalPickup: boolean;
  selected?: AgenticLogicShippingDeliveryOption;
  options: AgenticLogicShippingDeliveryOption[];
};

export type AgenticLogicShippingContext = {
  ${sdkNote("shipping.state")}state: "resolved" | "assumed" | "not-applicable";
  currency: string;
  ${sdkNote("shipping.freeShippingThreshold")}freeShippingThreshold?: number;
  ${sdkNote("shipping.remainingToFreeShipping")}remainingToFreeShipping?: number | null;
  ${sdkNote("shipping.tiers")}tiers?: AgenticLogicShippingTier[];
  ${sdkNote("shipping.delivery")}delivery?: AgenticLogicShippingDelivery;
};

export type AgenticLogicRequest = {
  surface: "onsite" | "checkout" | "post-purchase";
  placementId: string;
  ${note("cart")}cart: AgenticLogicCartData | null;
  ${note("order")}order: AgenticLogicOrderData | null;
  ${note("pageProduct")}pageProduct: AgenticLogicPageProduct | null;
  ${note("customer")}customer: AgenticLogicCustomerData | null;
  shipping: AgenticLogicShippingContext;
  cartFingerprint?: string;
  ${sdkNote("request.evaluatedAt")}evaluatedAt: number;
};

export type AgenticLogicContext = {
  isPreview: boolean;
  ${sdkNote("context.slotsNeeded")}slotsNeeded: number;
};

// ----- result side -----
export type AgenticLogicItemKind =
  | "product"
  | "variant"
  | "image"
  | "reviewRef"
  | "metaobjectRef"
  | "bundle";

export type AgenticLogicItemDiscount = { type: "percentage" | "fixed"; value: number };

export type AgenticLogicBundleChild = { kind: AgenticLogicItemKind; id: string; quantity: number };

export type AgenticLogicResultItem = {
  kind: AgenticLogicItemKind;
  id: string;
  defaultVariantId?: string;
  ${sdkNote("resultItem.allowedVariantIds")}allowedVariantIds?: string[];
  ${sdkNote("resultItem.sellingPlanId")}sellingPlanId?: string;
  ${sdkNote("resultItem.intent")}intent?: "add" | "replace";
  ${sdkNote("resultItem.discount")}discount?: AgenticLogicItemDiscount;
  sourceBranch?: string;
  children?: AgenticLogicBundleChild[];
};

export type AgenticLogicBranchFired = { base: string; instance?: string };

export type AgenticLogicResultEnvelope = {
  version: 1;
  items: AgenticLogicResultItem[];
  branchFired?: AgenticLogicBranchFired;
  cartFingerprint?: string;
  warnings?: string[];
  /**
   * Rule-authored metadata. Free-form and read by NOTHING — no key here is
   * interpreted. A rule decides WHICH products are recommended; it never
   * prices them. The discount and the struck-through price belong to the
   * placement this rule is attached to, where the merchant sets them.
   */
  metadata?: { [key: string]: unknown };
};

// ----- SDK read shapes -----
// Shopper-visible product data comes from Shopify Storefront GraphQL in the
// request country. Admin and ShopifyQL can select ordered product IDs for
// inventory, publication-date, attribute, and analytics accessors; those IDs
// are hydrated through Storefront before rule code receives them.
export type AgenticLogicMetafieldRef = { namespace: string; key: string };

export type AgenticLogicStorefrontProductFilter = {
  available?: boolean;
  variantOption?: { name: string; value: string };
  category?: { id: string };
  taxonomyMetafield?: { namespace: string; key: string; value: string };
  productType?: string;
  productVendor?: string;
  /** Integer minor-unit bounds in the shop currency. */
  price?: { min?: number; max?: number };
  productMetafield?: { namespace: string; key: string; value: string };
  variantMetafield?: { namespace: string; key: string; value: string };
  tag?: string;
};

/**
 * Predicates that CANNOT run in Storefront product search, so they run over
 * fetched pages. Every key here turns the query into a bounded paged scan —
 * set \`scan\` explicitly when you use one.
 */
export type AgenticLogicProductPostFetchWhere = {
  /** Substring anywhere in the title, case-insensitive. */
  title?: { contains: string };
  /**
   * Tag predicate on the scan path. This is the ONLY way to require several
   * tags at once inside a collection. Case-sensitive: use the exact Shopify tag.
   * That is this CODE lane; the template lane's in-memory tag filters
   * (\`collection\`, \`shopify-recommendations\`, and \`tag-query\`'s none-of) ignore
   * case instead.
   */
  tags?: { anyOf?: string[]; allOf?: string[] };
  metafields?: Array<
    AgenticLogicMetafieldRef & { equals?: string; in?: string[]; exists?: boolean }
  >;
  /**
   * Option-value predicate on the scan path. The option NAME and its values
   * both match case-insensitively (unlike \`tags\`): 'Color' matches 'color',
   * 'Blue' matches 'blue'. Case is the only tolerance — names are otherwise
   * exact, so 'Color' and 'Colour' are DIFFERENT options. Entries in this
   * array are ANDed, so a second entry cannot cover the other spelling; give
   * each spelling its own attempt in a \`firstNonEmpty\` ladder.
   */
  optionValues?: Array<{ name: string; anyOf: string[] }>;
};

/**
 * Predicates Storefront product search evaluates server-side. Facets AND
 * together; the values of one facet OR together.
 *  - \`tags\`: \`anyOf\` is any listed tag, \`allOf\` requires every listed tag —
 *    both are search terms, inside a collection too. Tag matching ignores case.
 *  - \`title.prefix\`: a leading-word prefix match on the title. A substring
 *    anywhere in the title is \`where.postFetch.title.contains\`.
 *  - \`price\`: integer minor units of the shop currency, against the
 *    product's variant prices.
 *
 * \`productFilters\` are valid only with \`collectionId\`. A collection does
 * not support \`title\` or \`tags.allOf\` server predicates; use the matching
 * post-fetch predicate for those combinations.
 */
export type AgenticLogicCatalogServerWhere = {
  tags?: { anyOf?: string[]; allOf?: string[] };
  productType?: { anyOf: string[] };
  vendor?: { anyOf: string[] };
  ${sdkNote("serverWhere.price")}price?: { gte?: number; gt?: number; lte?: number; lt?: number };
  title?: { prefix?: string };
};

export type AgenticLogicCollectionServerWhere = {
  tags?: { anyOf?: string[] };
  productType?: { anyOf: string[] };
  vendor?: { anyOf: string[] };
  ${sdkNote("serverWhere.price")}price?: { gte?: number; gt?: number; lte?: number; lt?: number };
  productFilters?: AgenticLogicStorefrontProductFilter[];
};

/**
 * Storefront serves \`bestSelling\`, \`price\`, \`createdAt\`, \`title\`, and
 * \`relevance\`. \`inventoryTotal\` is only valid on an Admin-backed specialized
 * accessor such as \`getInStock\`.
 */
export type AgenticLogicProductOrderBy = {
  field: "bestSelling" | "price" | "createdAt" | "title" | "relevance" | "inventoryTotal";
  direction?: "asc" | "desc";
};

export type AgenticLogicStorefrontProductOrderBy = {
  field: "bestSelling" | "price" | "createdAt" | "title" | "relevance";
  direction?: "asc" | "desc";
};

/**
 * The typed product read. Exactly one of these selection forms:
 *  - \`ids\`: the products named, nothing else (no scope, filter, sort, scan);
 *  - \`collectionId\` and/or \`where\`: a Storefront product search in the
 *    request country. Shopify omits products outside the applicable catalog.
 * A post-fetch query can issue several Storefront page reads and returns
 * \`scan\` telemetry.
 */
type AgenticLogicProductQueryBase = {
  ids?: string[];
  orderBy?: AgenticLogicStorefrontProductOrderBy;
  ${sdkNote("query.limit")}limit: number;
  ${sdkNote("query.scan")}scan?: { pageSize?: number; maxProducts?: number };
  include?: {
    collectionIds?: boolean;
    metafields?: AgenticLogicMetafieldRef[];
  };
};

export type AgenticLogicProductQuery = AgenticLogicProductQueryBase &
  (
    | {
        collectionId: string;
        where?: {
          server?: AgenticLogicCollectionServerWhere;
          postFetch?: AgenticLogicProductPostFetchWhere;
        };
      }
    | {
        collectionId?: undefined;
        where?: {
          server?: AgenticLogicCatalogServerWhere;
          postFetch?: AgenticLogicProductPostFetchWhere;
        };
      }
  );

/**
 * Restricts a catalog read to collections and/or tags. Both given = products
 * in ANY listed collection AND carrying ANY listed tag. Tag matching ignores
 * case. Collection ids are GIDs (\`gid://shopify/Collection/…\`).
 */
export type AgenticLogicCatalogScope = {
  collectionIds?: string[];
  tags?: string[];
};

/** Trailing window a time-based read covers. */
export type AgenticLogicTimeWindow = "24h" | "7d" | "30d" | "90d";

/**
 * Options for \`sdk.getBestSellers\`. The ranking comes from Shopify analytics
 * (ShopifyQL \`sales\` schema), which lag live orders by minutes to hours.
 */
export type AgenticLogicBestSellersQuery = {
  /** Default \`"30d"\`. */
  timeWindow?: AgenticLogicTimeWindow;
  /** \`"units"\` (default, net items sold) or \`"revenue"\` (net sales). */
  metric?: "units" | "revenue";
  /**
   * Tag scope narrows the ranking itself; collection scope filters the ranked
   * products, so a tight collection can return fewer than \`limit\`.
   */
  scope?: AgenticLogicCatalogScope;
  /** Products to return, in rank order. Integer 1–250. */
  limit: number;
  /** Applied after ranking, so the list can shrink below \`limit\`. */
  excludeOutOfStock?: boolean;
};

/**
 * Options for \`sdk.getInStock\`. Inventory-COUNT based: a product whose
 * inventory is not tracked has no total and is never returned. To keep
 * sellable-but-untracked products, read \`variants[].availableForSale\`
 * instead.
 */
export type AgenticLogicInStockQuery = {
  /** Minimum total inventory across locations, inclusive. Default 1. */
  minQuantity?: number;
  scope?: AgenticLogicCatalogScope;
  /** Default: highest inventory first (\`inventoryTotal\` desc). */
  orderBy?: AgenticLogicProductOrderBy;
  /** Integer 1–250. */
  limit: number;
};

/** Options for \`sdk.getNewArrivals\`. */
export type AgenticLogicNewArrivalsQuery = {
  /** Default \`"30d"\`. */
  timeWindow?: AgenticLogicTimeWindow;
  /** \`"created"\` (default) or \`"published"\` — which date the window is over. */
  dateField?: "created" | "published";
  scope?: AgenticLogicCatalogScope;
  /** Products to return, newest first. Integer 1–250. */
  limit: number;
  /** Applied after the read, so the list can shrink below \`limit\`. */
  excludeOutOfStock?: boolean;
};

/** Options for \`sdk.getRelatedByAttribute\` (the seed product id is the first argument). */
export type AgenticLogicRelatedByAttributeQuery = {
  /** Which attribute of the seed product the candidates must share. */
  by: "tag" | "productType" | "vendor" | "collection";
  /**
   * \`price\` / \`bestSelling\` sort server-side only when \`by\` is
   * \`"collection"\` and the seed is in exactly one collection; otherwise
   * post-fetch.
   */
  orderBy?: AgenticLogicProductOrderBy;
  /** Integer 1–250. */
  limit: number;
  /** Applied after the read, so the list can shrink below \`limit\`. */
  excludeOutOfStock?: boolean;
};

/**
 * Post-fetch narrowing for \`sdk.getProductRecommendations\`. Shopify's
 * recommendation read takes no filters, so every option here runs over the
 * rows Shopify returned and can only SHRINK the list.
 */
export type AgenticLogicRecommendationOptions = {
  /** Keep at most this many, in Shopify's order. Integer 1–250. */
  limit?: number;
  scope?: AgenticLogicCatalogScope;
  excludeOutOfStock?: boolean;
};

export type AgenticLogicProductVariant = {
  id: string;
  title: string;
  price: number;
  compareAtPrice?: number | null;
  availableForSale: boolean;
  selectedOptions: AgenticLogicSelectedOption[];
};

export type AgenticLogicProduct = {
  id: string;
  title: string;
  handle: string;
  vendor: string;
  productType: string;
  tags: string[];
  featuredImageUrl: string | null;
  currency: string;
  minPrice: number;
  maxPrice: number;
  variants: AgenticLogicProductVariant[];
  options: Array<{ name: string; values: string[] }>;
  metafields?: Array<AgenticLogicMetafieldRef & { value: string | null }>;
  /**
   * Storefront-visible collection GIDs, present only when the read asked for
   * them. ABSENT is not the same statement as EMPTY:
   * absent means collections were not read, empty means the product is in none.
   */
  collectionIds?: string[];
};

export type AgenticLogicProductPage = {
  items: AgenticLogicProduct[];
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
  ${sdkNote("page.scan")}scan?: { pagesScanned: number; productsScanned: number; truncated: boolean };
};

export type AgenticLogicSdkCustomer = {
  id: string;
  ordersCount: number;
  amountSpent?: AgenticLogicCustomerAmountSpent;
  tags: string[];
  createdAt?: string;
  lastOrderAt?: string | null;
};

export type AgenticLogicProductCost = {
  productId: string;
  currency: string;
  unitCost: number | null;
  variants: Array<{ variantId: string; unitCost: number }>;
};

${datasetKeyType}export type AgenticLogicOrgExclusions = {
  productIds: string[];
  tags: string[];
  collections: string[];
  vendors: string[];
};

export type AgenticLogicAvailability = {
  productId: string;
  variantId?: string;
  availableForSale: boolean;
  quantityAvailable: number | null;
};

export type AgenticLogicAttemptRecord = {
  base: string;
  instance?: string;
  contributed: number;
  outcome: "contributed" | "empty" | "not-selected" | "skipped" | "error";
};

export type AgenticLogicChainResult = {
  items: AgenticLogicResultItem[];
  attempts: AgenticLogicAttemptRecord[];
};

// ----- the ambient SDK -----
// \`sdk\`, \`attempt\`, \`firstNonEmpty\` and \`fillTo\` resolve ambiently in every
// rule file — the imports are auto-injected at bundle/test time, so none of
// them is ever imported by hand. Their SHAPES live here so they read as
// ordinary named types; the injected sdk.d.ts only binds the four names.

/** Shopify's required recommendation strategy. */
export type AgenticLogicRecommendationIntent = "RELATED" | "COMPLEMENTARY";

/** How \`fillTo\` decides two items are the same when de-duplicating. */
export type AgenticLogicDedupGrain = "product" | "product-variant";

export type AgenticLogicSdk = {
  /**
   * The typed Storefront product read in the request country. See
   * \`AgenticLogicProductQuery\` for selection, filter, sort, and scan forms.
   */
  getProducts(query: AgenticLogicProductQuery): Promise<AgenticLogicProductPage>;
  /**
   * Top sellers over a trailing window (\`"24h" | "7d" | "30d" | "90d"\`,
   * default 30d), ranked by Shopify analytics and hydrated from Storefront.
   * Prices and publication use the request country. The ranking lags live orders by
   * minutes to hours. \`excludeOutOfStock\` and a collection \`scope\` apply
   * AFTER ranking, so the list can come back shorter than \`limit\`.
   *
   * The \`read_reports\` grant this needs is a required scope at install, so
   * every store has it — never ask the merchant to confirm analytics access.
   * If a ranking cannot be produced the read THROWS a denial whose reason is
   * \`"shopifyql_unavailable"\` — never an empty list. A denial is an SDK
   * error, and the chain rethrows every SDK error: it FAILS the invocation
   * rather than falling through to the next attempt. A \`firstNonEmpty\`
   * ladder still belongs here for the ranking's OWN empty result (a scope
   * with no sales in the window), which does fall through.
   */
  getBestSellers(opts: AgenticLogicBestSellersQuery): Promise<AgenticLogicProduct[]>;
  /**
   * Products whose total tracked inventory (summed across locations) is at
   * least \`minQuantity\` (default 1), highest inventory first unless
   * \`orderBy\` says otherwise. Admin selects the IDs because Storefront cannot
   * server-filter by the threshold; Storefront supplies the product records.
   */
  getInStock(opts: AgenticLogicInStockQuery): Promise<AgenticLogicProduct[]>;
  /**
   * Active, published products created (or, with \`dateField: "published"\`,
   * published) within the trailing window (default 30d), newest first.
   * \`excludeOutOfStock\` applies after the read, so the list can shrink
   * below \`limit\`.
   */
  getNewArrivals(opts: AgenticLogicNewArrivalsQuery): Promise<AgenticLogicProduct[]>;
  /**
   * Other products sharing one attribute with the seed product — any of its
   * tags, its product type, its vendor, or any of its collections. The seed
   * itself is never in the result. Admin selects IDs and Storefront supplies
   * the product records.
   */
  getRelatedByAttribute(
    productId: string,
    opts: AgenticLogicRelatedByAttributeQuery
  ): Promise<AgenticLogicProduct[]>;
  /**
   * Shopify's related / complementary recommendations for ONE seed product
   * (Storefront-backed). Resolves the same
   * product shape \`getProducts\` does, plus \`collectionIds\`. The optional
   * \`limit\`, \`scope\` and \`excludeOutOfStock\` narrow POST-FETCH — they can
   * only shrink Shopify's list, never widen it. \`COMPLEMENTARY\` is empty
   * unless the merchant configured complementary products in Search &
   * Discovery.
   */
  getProductRecommendations(
    productId: string,
    opts: { intent: AgenticLogicRecommendationIntent } & AgenticLogicRecommendationOptions
  ): Promise<AgenticLogicProduct[]>;
  /**
   * Escape hatch for a read the typed accessors cannot express — prefer a
   * typed accessor whenever one can express the read. Use it only for a
   * Storefront field that the typed accessors do not expose.
   *
   * The document MUST be a SINGLE query operation: mutations and
   * multi-operation documents are REFUSED before the read runs.
   *
   * It MUST declare \`$country: CountryCode!\` and apply
   * \`@inContext(country: $country)\`. The platform does not add them for you —
   * the read is refused without them.
   *
   * Never pass \`country\` in \`variables\`: the platform supplies the value and
   * overwrites yours.
   *
   * The read resolves the operation's \`data\` OBJECT, already unwrapped —
   * NOT the GraphQL \`{data, errors}\` envelope. There is no \`data\` key on
   * the result: \`raw.data\` is ALWAYS \`undefined\`. Index the operation's own
   * root fields directly. A failed read THROWS; nothing is ever reported to
   * you as \`errors\`. The value is UNVALIDATED (\`unknown\`), unlike every
   * typed accessor — narrow it yourself.
   *
   * Example — ${AGENTIC_LOGIC_RAW_QUERY_EXAMPLE_OPERATION}
   * resolves to ${AGENTIC_LOGIC_RAW_QUERY_EXAMPLE_RESULT}, so the products are
   * at \`(raw as { products?: { nodes?: unknown[] } }).products?.nodes\`.
   */
  getProductsRaw(gql: string, variables?: Record<string, unknown>): Promise<unknown>;
  getCustomer(): Promise<AgenticLogicSdkCustomer | null>;
  getProductCosts(productIds: string[]): Promise<AgenticLogicProductCost[]>;
  ${getDatasetAccessor}${getSourceAccessor}${sdkNote("sdk.getOrgExclusions")}getOrgExclusions(): Promise<AgenticLogicOrgExclusions>;
  ${sdkNote("availability.ids")}checkAvailability(
    ids: Array<{ productId: string; variantId?: string }>
  ): Promise<AgenticLogicAvailability[]>;
  ${sdkNote("sdk.seededShuffle")}seededShuffle<T>(items: readonly T[]): T[];
};

/** What an attempt's body may return: bare items, or a nested chain result. */
export type AgenticLogicAttemptResult =
  | AgenticLogicResultItem[]
  | AgenticLogicChainResult;

/** One named, lazily-run branch. Build with \`attempt\`, run with a combinator. */
export type AgenticLogicAttempt = {
  readonly base: string;
  /**
   * WHICH PASS of the branch this candidate is — the tag, theme, collection
   * or seed product this run of the same logic is for.
   *
   * Drill-in data, never identity: it surfaces as
   * \`telemetry.attempts[].instance\` and \`envelope.branchFired.instance\`,
   * and is NEVER fused into \`base\`. The merchant sees one branch row with
   * one instance row per pass.
   */
  readonly instance?: string;
  run(): Promise<AgenticLogicAttemptResult>;
};

/**
 * Declares a branch without running it.
 *
 * \`base\` is a STATIC STRING LITERAL and IS the branch node id the merchant
 * sees on the rule canvas. Never an identifier, never an interpolated
 * template — the graph, the publish gate and \`branchFired\` telemetry all
 * join on it.
 *
 * When the SAME selection logic runs once per element of a list — tags,
 * themes, collections, seed products — keep ONE base and pass the element as
 * \`{ instance }\`:
 *
 *     ${AGENTIC_LOGIC_INSTANCED_ATTEMPT_EXAMPLE}
 *
 * Writing one \`attempt()\` per element with a DIFFERENT base is the
 * documented anti-pattern. It is N branch nodes for one loop body: the canvas
 * grows with the list, and the readout shows N rows to reconcile instead of
 * one row with N instance rows.
 *
 * COST: an instanced ladder is priced at the per-invocation attempt ceiling
 * UNLESS its width is provable from the source — the candidate list must come
 * from an array literal (\`["a","b","c"].map(...)\`, a \`const\` bound once to
 * one, or \`for (const x of [...])\`) or from a literal cap on any list
 * (\`xs.slice(0, N)\`, which bounds the ladder whatever \`xs\` holds). A list
 * computed from the request, or spread from another array, cannot be proved
 * and prices at the ceiling, so keep the literal literal — and note the cap is
 * the SAME idiom the read budget asks for, so one \`.slice(0, N)\` answers
 * both.
 */
export type AgenticLogicAttemptFn = (
  base: string,
  fn: () => Promise<AgenticLogicAttemptResult>,
  opts?: { instance?: string }
) => AgenticLogicAttempt;

/** Runs attempts in order, stopping at the first that contributes items. */
export type AgenticLogicFirstNonEmptyFn = (
  attempts: readonly AgenticLogicAttempt[]
) => Promise<AgenticLogicChainResult>;

/** Runs attempts until \`n\` de-duplicated items are collected. */
export type AgenticLogicFillToFn = (
  n: number,
  attempts: readonly AgenticLogicAttempt[],
  opts?: { dedupGrain?: AgenticLogicDedupGrain }
) => Promise<AgenticLogicChainResult>;

// ----- the rule entry contract -----
export type AgenticLogicInvocation = {
  request: AgenticLogicRequest;
  context: AgenticLogicContext;
  secrets: Record<string, string>;
};

export type AgenticLogicEvaluateFn = (
  invocation: AgenticLogicInvocation
) => Promise<AgenticLogicResultEnvelope>;
`;
}

/**
 * The projection without the org-data surface — the static default the
 * bundler config ships. The bundler only strips types, so this is never
 * type-checked against a rule and gates nothing: a published rule that reads
 * a dataset or a Source still evaluates. Generation-time consumers (Monaco,
 * the authoring model) pass the author's own {@link AgenticLogicDataSurface}
 * to {@link buildAgenticLogicTypesModuleSource} instead.
 */
export const AGENTIC_LOGIC_TYPES_MODULE_SOURCE =
  buildAgenticLogicTypesModuleSource({
    // No per-field notes, deliberately. This constant is what the BUNDLER
    // ships into a published rule, where the types are compiled and the doc
    // comments are never read by anyone. The editor- and model-facing callers
    // render real notes and pass them in.
    dataSurface: AGENTIC_LOGIC_DATA_SURFACE_OFF,
  });

/**
 * The injected `sdk.ts` module for user-authored rule workspaces, plus the
 * ambient declarations editors see.
 *
 * DESIGN: the injected module is a THIN BINDING, not a second SDK
 * implementation. The real SDK (builder compiler, chain combinators, seeded
 * shuffle, port delegation) lives in ./rec-sdk.ts as ordinary imported code;
 * the runner materializes it and publishes one runtime object on a global
 * bridge BEFORE importing rule code, and this module re-exports the ambient
 * names from that bridge. One implementation, two entry forms — the CSP
 * types/logger split, extended to an object with behavior.
 *
 * The bridge global is runner-owned wiring, never rule-visible API: the
 * source guard's denylist keeps rule code from importing host modules, and
 * reaching for the bridge directly instead of the injected `sdk` binding
 * yields the same object anyway — there is nothing extra to steal on it.
 */

export const AGENTIC_LOGIC_SDK_BRIDGE_GLOBAL =
  "__intelligemsAgenticLogicSdkRuntime__";

/**
 * A zero-argument getter the runner publishes ONCE per container, returning
 * the runtime bound to the caller's async context (or `undefined` outside a
 * rule evaluation).
 *
 * It exists because the bridge global alone is last-writer-wins. A rule whose
 * evaluation exceeds the deadline is abandoned, not cancelled: its promise is
 * still parked on an in-flight SDK read when the handler returns and the
 * container freezes. On thaw — during the NEXT invocation — that continuation
 * resumes and calls `sdk.*` again, and a call-time read of the bridge would
 * hand it the live runtime of an unrelated shopper's invocation, spending its
 * read budget and splicing its warnings and dataset pins into the envelope
 * that invocation serves. Async context follows the abandoned continuation,
 * so this resolves it back to its OWN runtime, whose invocation-state record
 * has already expired — it fails closed instead of stealing.
 *
 * A getter, not the AsyncLocalStorage itself: rule code must not reach
 * `enterWith`/`run`. Both this name and the bridge are on the source guard's
 * dangerous-construct denylist, and both are published non-enumerable and
 * non-writable so they can be neither enumerated nor swapped.
 */
export const AGENTIC_LOGIC_SDK_SCOPE_GLOBAL =
  "__intelligemsAgenticLogicSdkScope__";

export const AGENTIC_LOGIC_SDK_MODULE_SOURCE = `// Injected by the Intelligems build — do not edit.
// Binds the ambient SDK names to the runtime the runner initialized.
function currentRuntime() {
  const scope = globalThis.${AGENTIC_LOGIC_SDK_SCOPE_GLOBAL};
  const scoped = typeof scope === "function" ? scope() : undefined;
  const runtime = scoped ?? globalThis.${AGENTIC_LOGIC_SDK_BRIDGE_GLOBAL};
  if (!runtime) {
    throw new Error(
      "Recommendations SDK runtime is not initialized: the runner must set " +
        "globalThis.${AGENTIC_LOGIC_SDK_BRIDGE_GLOBAL} before importing rule code."
    );
  }
  return runtime;
}

// Fail at import time when runner wiring is absent, while resolving every
// callable against the current invocation rather than a cached module's first.
currentRuntime();
export const sdk = new Proxy({}, {
  get(_target, property) {
    const currentSdk = currentRuntime().sdk;
    const value = Reflect.get(currentSdk, property);
    return typeof value === "function" ? value.bind(currentSdk) : value;
  },
});
export const attempt = (base, fn, opts) => currentRuntime().attempt(
  base,
  async () => {
    const result = await fn();
    globalThis.logger.info(\`Chain attempt "\${base}" returned\`, {
      ...(opts?.instance === undefined ? {} : { instance: opts.instance }),
      result,
    });
    return result;
  },
  opts,
);
export const firstNonEmpty = (...args) => currentRuntime().firstNonEmpty(...args);
export const fillTo = (...args) => currentRuntime().fillTo(...args);
`;

/**
 * Ambient declarations for the editor: rule files use `sdk`, `attempt`,
 * `firstNonEmpty`, and `fillTo` without importing them (the imports are
 * auto-injected at bundle/test time, mirroring the CSP `logger` pattern).
 *
 * This file BINDS the four ambient names and nothing else — every shape it
 * names is defined in the injected `./types` module, under its own
 * "the ambient SDK" section. Two reasons it is split that way rather than
 * spelling the signatures out here: this file must stay a script (no
 * top-level import/export) or `declare` stops being global, which forced an
 * `import("./types").X` on every single member; and `types.ts` is the locked
 * tab a rule author actually reads, so the SDK surface belongs there next to
 * the shapes it returns.
 *
 * `declare const` rather than `declare function` for the three combinators
 * because that is what the injected runtime module really exports
 * ({@link AGENTIC_LOGIC_SDK_MODULE_SOURCE} exports them as consts).
 */
export const AGENTIC_LOGIC_SDK_AMBIENT_DTS = `declare const sdk: import("./types").AgenticLogicSdk;
declare const attempt: import("./types").AgenticLogicAttemptFn;
declare const firstNonEmpty: import("./types").AgenticLogicFirstNonEmptyFn;
declare const fillTo: import("./types").AgenticLogicFillToFn;
`;

/**
 * Ambient declaration for the hidden `logger`. The runtime `./logger` module is
 * never shown to the user (no tab, no import in any visible buffer); this
 * `declare const logger` extra-lib makes `logger.*` type-check in every editor
 * file while the import is auto-injected at bundle/test time.
 *
 * The method surface mirrors the emitter the marker-logger protocol builds, and
 * `details` is the log entry's own `unknown` payload — not `any`, so a rule
 * cannot read fields off what it logged.
 */
export const AGENTIC_LOGIC_LOGGER_AMBIENT_DTS = `declare const logger: {
  log(message: string, details?: unknown): void;
  info(message: string, details?: unknown): void;
  warn(message: string, details?: unknown): void;
  error(message: string, details?: unknown): void;
  debug(message: string, details?: unknown): void;
};
`;

/**
 * The seed buffer for a new `code` rule.
 *
 * Two contracts it has to honour, both enforced elsewhere and both easy to get
 * wrong by hand: the entry is a NAMED `export async function evaluate`, not a
 * default export; and the runtime prelude hard-blocks ambient time, randomness
 * and network access, so a starter reaching for `Date.now`, `Math.random` or
 * `fetch` would throw on its first run.
 */
export const AGENTIC_LOGIC_STARTER_SOURCE = `import type {
  AgenticLogicInvocation,
  AgenticLogicResultEnvelope,
} from "./types";

export async function evaluate({
  request,
  context,
}: AgenticLogicInvocation): Promise<AgenticLogicResultEnvelope> {
  // Use logger.log, logger.info, logger.warn, logger.error, or logger.debug for
  // output you want captured in publish gates and live runs. Ordinary console.*
  // output is ignored.
  logger.info("Selecting recommendations", {
    surface: request.surface,
    placementId: request.placementId,
    slotsNeeded: context.slotsNeeded,
  });

  // LOAD-BEARING, not boilerplate: a placement can resolve to zero slots and
  // the invocation is delivered rather than refused. SDK query limits are
  // positive, so a rule that reads first would fail the read; answer with an
  // empty envelope before touching the catalog.
  if (context.slotsNeeded <= 0) {
    return { version: 1, items: [] };
  }

  // The branch this rule answers with. Report it back as \`branchFired\` below:
  // the platform never infers it, so a rule that omits it has no branch-level
  // provenance and its coverage collapses to input → output no matter what ran.
  // When the rule uses \`attempt()\`, this string must be that attempt's base.
  const branch = "best-sellers";

  // Rules must be deterministic: ambient time, randomness, and network access
  // are blocked at runtime. Read through \`sdk\`, and use sdk.seededShuffle to
  // vary an order ACROSS SHOPPERS — it is seeded per shopper, so one shopper
  // sees the same order on every visit; read \`request.evaluatedAt\` if the
  // order must change over time.
  // Post-fetch work in rule code THROWS CANDIDATES AWAY — the cart filter
  // below, and anything else you exclude by hand. A read sized at exactly
  // \`context.slotsNeeded\` has nothing left to backfill with, so every discard
  // is a slot the placement never fills. Ask for headroom over what you render.
  // The publish-time scan-cost gate judges this from BOTH sides: far wider than
  // \`slotsNeeded * 4 + 20\` is a read you pay for and never use; exactly
  // \`slotsNeeded\` under-fills the block.
  const limit = Math.min(250, context.slotsNeeded * 4 + 20);

  // Tagged best sellers: a plain Storefront product search. For a shop-wide,
  // time-windowed popularity ranking use \`sdk.getBestSellers\` instead —
  // \`getProducts\` cannot order the whole catalogue by best-selling. Its
  // \`read_reports\` grant is required at install so every store has it; it
  // throws a typed denial rather than returning empty when it cannot rank,
  // so keep a fallback attempt behind it.
  const page = await sdk.getProducts({
    where: { server: { tags: { anyOf: ["best-seller"] } } },
    limit,
  });

  // Do not recommend what the shopper already has.
  const cartProductIds = new Set(
    request.cart?.lines.map((line) => line.productId) ?? []
  );

  // Filling from more than one source? \`fillTo(context.slotsNeeded, [attempt(…),
  // attempt(…)])\` accumulates across attempts and de-dupes for you.
  return {
    version: 1,
    branchFired: { base: branch },
    items: page.items
      .filter((product) => !cartProductIds.has(product.id))
      // The read is wider than the block on purpose; return only what fits.
      .slice(0, context.slotsNeeded)
      .map((product) => ({
        kind: "product",
        id: product.id,
      })),
  };
}
`;
